import type { Table } from '@tanstack/react-table';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ui/components';
import { Settings2Icon, Trash2Icon } from 'lucide-react';
import { useAppTableContext } from '../../../../lib/context/use-app-table-context';

interface ViewButtonProps<TData> {
  table: Table<TData>;
}

export function ViewButton<TData>({ table }: ViewButtonProps<TData>) {
  const { templates, selectedTemplateId, onSelectTemplate, onDeleteTemplate } =
    useAppTableContext();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label="Toggle columns"
          className="ml-auto hidden h-8 px-2 lg:flex"
          size="sm"
          variant="outline"
        >
          <Settings2Icon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-96 overflow-auto"
      >
        {!!templates?.length && (
          <>
            <DropdownMenuLabel>Templates</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {templates?.map((template) => (
              <DropdownMenuCheckboxItem
                key={template.id}
                checked={selectedTemplateId === template.id}
                className="group flex items-center justify-between"
                onSelect={(e) => {
                  e.preventDefault();
                  onSelectTemplate?.(template);
                }}
              >
                {template.title}

                <div
                  className="opacity-0 transition-opacity group-hover:opacity-100"
                  role="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onDeleteTemplate?.(template);
                  }}
                >
                  <Trash2Icon className="size-4 text-destructive" />
                </div>
              </DropdownMenuCheckboxItem>
            ))}
          </>
        )}
        <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {table
          .getAllColumns()
          .filter(
            (column) =>
              column.getCanHide() &&
              column.id !== 'select' &&
              column.id !== 'actions',
          )
          .map((column) => {
            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                checked={column.getIsVisible()}
                className="capitalize"
                onCheckedChange={(value) => column.toggleVisibility(!!value)}
                onSelect={(e) => e.preventDefault()}
              >
                {column.columnDef.meta?.name ?? column.id}
              </DropdownMenuCheckboxItem>
            );
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
